import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Edit3, Plus, Trash2, UserRound } from "lucide-react";

import {
  CompactPrimaryButton,
  PrimaryButton,
  SecondaryButton,
} from "../../components/Button/Buttons";
import Input from "../../components/Inputs/Input";
import Select from "../../components/Inputs/Select";
import TextArea from "../../components/Inputs/TextArea";
import Loader from "../../components/Loader/Loader";
import SettingsPageShell from "../../components/Settings/SettingsPageShell";
import ConfirmDialog from "../../components/shared/ConfirmDialog/ConfirmDialog";
import Toggle from "../../components/shared/Toggle";
import { useTheme } from "../../context/ThemeProvider";
import ColorSchemePicker from "../../components/Common/ColorSchemePicker";
import useFeedback from "../../hooks/useFeedback";
import {
  createStyle,
  deleteStyle,
  getProfilePreferences,
  setActiveStyle,
  updateLocalizationPreferences,
  updateStyle,
} from "../../api/preferences";

const PROFILE_QUERY_KEY = ["profile-preferences"];
const EMPTY_STYLE = {
  name: "",
  response_length: "balanced",
  tone: "professional",
  formatting: { headings: true, bullet_points: true, tables: true },
  citation_preference: "when_available",
  prompt: "",
};
const LENGTH_OPTIONS = [
  { value: "concise", label: "Concise" },
  { value: "balanced", label: "Balanced" },
  { value: "detailed", label: "Detailed" },
];
const TONE_OPTIONS = [
  { value: "casual", label: "Casual" },
  { value: "professional", label: "Professional" },
  { value: "formal", label: "Formal" },
];
const FORMATTING_FIELDS = [
  ["headings", "Headings", "Split longer answers into titled sections"],
  ["bullet_points", "Bullet points", "Prefer lists over long paragraphs"],
  ["tables", "Tables", "Use tables for comparisons and structured data"],
];
const CITATION_OPTIONS = [
  { value: "never", label: "Never" },
  { value: "when_available", label: "When available" },
  { value: "always_when_available", label: "Always when available" },
];
const LOCALE_OPTIONS = [
  { value: "en-US", label: "English (United States)" },
  { value: "en-GB", label: "English (United Kingdom)" },
  { value: "es-ES", label: "Spanish (Spain)" },
  { value: "fr-FR", label: "French (France)" },
  { value: "de-DE", label: "German (Germany)" },
  { value: "hi-IN", label: "Hindi (India)" },
];

const Segmented = ({ value, onChange, options, label }) => (
  <div role="group" aria-label={label} className="inline-flex items-center gap-1 p-1 rounded-lg bg-fill-hex-e8eaef border-[0.5px] border-edge-hex-d8d8d8 dark:bg-fill-dialog-border dark:border-edge-hex-aaaaaa50">
    {options.map((option) => (
      <button
        key={option.value}
        type="button"
        onClick={() => onChange(option.value)}
        aria-pressed={value === option.value}
        className={`px-3 py-1.5 text-[13px] rounded-md transition-colors ${
          value === option.value
            ? "bg-fill-white dark:bg-fill-dialog-surface text-ink-primary shadow-sm font-medium"
            : "text-ink-gray-500 hover:text-ink-gray-700 dark:text-ink-gray-400 dark:hover:text-ink-gray-300"
        }`}
      >
        {option.label}
      </button>
    ))}
  </div>
);

const Field = ({ label, hint, children }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-ink-gray-400">{hint}</p>}
  </div>
);

const ProfileSection = ({ title, description, children }) => (
  <section className="py-6 border-b border-edge-gray-200 dark:border-edge-gray-700 last:border-0">
    <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-5">
      <div>
        <h2 className="text-sm font-semibold text-ink-gray-900 dark:text-ink-white">{title}</h2>
        <p className="mt-1 text-xs leading-5 text-ink-gray-500 dark:text-ink-gray-400">{description}</p>
      </div>
      <div>{children}</div>
    </div>
  </section>
);

const StyleEditor = ({ initialStyle, onSave, onCancel, saving }) => {
  const [style, setStyle] = useState(initialStyle || EMPTY_STYLE);
  const setField = (field, value) => setStyle((current) => ({ ...current, [field]: value }));
  const setFormatting = (field, value) => setStyle((current) => ({
    ...current,
    formatting: { ...current.formatting, [field]: value },
  }));

  return (
    <motion.div
      key="style-editor"
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.2 }}
      className="overflow-hidden"
    >
    <form
      onSubmit={(event) => { event.preventDefault(); onSave(style); }}
      className="space-y-4 mt-4 p-5 rounded-xl bg-fill-white dark:bg-fill-dialog-surface border border-edge-gray-200 dark:border-edge-dialog-border"
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-ink-gray-500 dark:text-ink-gray-400">
        {initialStyle?.style_id ? "Edit Style" : "New Style"}
      </p>
      <Field label="Name">
        <Input
          autoFocus
          required
          maxLength={80}
          value={style.name}
          onChange={(event) => setField("name", event.target.value)}
          placeholder="e.g. Executive summary"
        />
      </Field>
      <div className="flex flex-wrap gap-6">
        <Field label="Length">
          <Segmented label="Response length" value={style.response_length} onChange={(value) => setField("response_length", value)} options={LENGTH_OPTIONS} />
        </Field>
        <Field label="Tone">
          <Segmented label="Tone" value={style.tone} onChange={(value) => setField("tone", value)} options={TONE_OPTIONS} />
        </Field>
      </div>
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">Formatting</label>
        <p className="text-[11px] text-ink-gray-400">Turn on the elements Photon may use when writing a response.</p>
        <div className="mt-1 rounded-lg border border-edge-gray-200 dark:border-edge-dialog-border divide-y divide-edge-gray-200 dark:divide-edge-dialog-border">
          {FORMATTING_FIELDS.map(([field, label, description]) => (
            <div key={field} className="flex items-center justify-between gap-4 px-4 py-3">
              <div>
                <p className="text-[13px] text-ink-gray-900 dark:text-ink-white">{label}</p>
                <p className="text-[11px] text-ink-gray-500 dark:text-ink-gray-400">{description}</p>
              </div>
              <Toggle checked={style.formatting[field]} onChange={(next) => setFormatting(field, next)} />
            </div>
          ))}
        </div>
      </div>
      <Field label="Citations">
        <Select
          className="max-w-xs"
          value={style.citation_preference}
          onChange={(value) => setField("citation_preference", value)}
          options={CITATION_OPTIONS}
        />
      </Field>
      <Field label="Additional preferences">
        <TextArea
          rows={5}
          maxLength={2500}
          value={style.prompt}
          onChange={(event) => setField("prompt", event.target.value)}
          className="resize-y"
          placeholder="Describe how you want responses written."
        />
      </Field>
      <div className="flex items-start justify-between gap-4">
        <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400">System, safety, agent, tool, current-task, and Spectra instructions take precedence over My Style.</p>
        <p className="flex-shrink-0 text-[11px] text-ink-gray-400">{style.prompt.length}/2500 characters</p>
      </div>
      <div className="flex justify-end gap-3">
        <SecondaryButton type="button" onClick={onCancel}>Cancel</SecondaryButton>
        <PrimaryButton type="submit" disabled={saving || !style.name.trim()}>{saving ? "Saving…" : "Save style"}</PrimaryButton>
      </div>
    </form>
    </motion.div>
  );
};

const ProfilePage = () => {
  const { theme, toggleTheme, colorScheme, setColorScheme, appearanceError } = useTheme();
  const queryClient = useQueryClient();
  const { showFeedback } = useFeedback();
  const [editing, setEditing] = useState(null);
  const [deletingStyle, setDeletingStyle] = useState(null);
  const { data: profile, isLoading, error } = useQuery({
    queryKey: PROFILE_QUERY_KEY,
    queryFn: getProfilePreferences,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
  const mutation = useMutation({
    mutationFn: async ({ action, payload }) => {
      if (action === "create") return createStyle(payload);
      if (action === "update") return updateStyle(editing.style_id, payload);
      if (action === "delete") return deleteStyle(payload);
      if (action === "activate") return setActiveStyle(payload);
      if (action === "locale") return updateLocalizationPreferences(payload);
      return null;
    },
    onMutate: async ({ action, payload }) => {
      if (action !== "locale") return undefined;
      await queryClient.cancelQueries({ queryKey: PROFILE_QUERY_KEY });
      const previousProfile = queryClient.getQueryData(PROFILE_QUERY_KEY);
      queryClient.setQueryData(PROFILE_QUERY_KEY, (current) => current ? {
        ...current,
        localization: { ...current.localization, locale: payload },
      } : current);
      return { previousProfile };
    },
    onSuccess: () => { setEditing(null); setDeletingStyle(null); refresh(); showFeedback("Profile updated.", "success"); },
    onError: (requestError, variables, context) => {
      if (variables.action === "locale" && context?.previousProfile !== undefined) {
        queryClient.setQueryData(PROFILE_QUERY_KEY, context.previousProfile);
      }
      showFeedback(requestError?.response?.data?.message || "Could not save Profile settings", "error");
    },
  });

  if (isLoading) return <SettingsPageShell title="Profile"><div className="flex justify-center py-16"><Loader /></div></SettingsPageShell>;
  if (error) return <SettingsPageShell title="Profile"><p className="py-8 text-sm text-ink-red-600">Profile could not be loaded.</p></SettingsPageShell>;

  const identity = profile?.identity || {};
  const styles = profile?.styles || [];
  return (
    <SettingsPageShell title="Profile" description="Control how Photon looks and presents responses for your account." maxWidth="5xl">
      <ProfileSection title="Identity" description="Managed by your sign-in provider. Profile photos cannot be changed here.">
        <div className="flex items-center gap-4">
          {identity.picture ? <img src={identity.picture} alt="" className="h-14 w-14 rounded-full object-cover" /> : <div className="grid h-14 w-14 place-items-center rounded-full bg-surface-track-muted dark:bg-surface-track-muted"><UserRound className="h-6 w-6" /></div>}
          <div className="min-w-0"><p className="font-medium text-ink-gray-900 dark:text-ink-white">{identity.name || "Unnamed user"}</p><p className="truncate text-sm text-ink-gray-500 dark:text-ink-gray-400">{identity.email}</p></div>
        </div>
      </ProfileSection>
      <ProfileSection title="Appearance" description="Choose your color mode and scheme.">
        <div className="flex flex-wrap gap-4">
          <Field label="Color mode">
            <Segmented label="Color mode" value={theme} onChange={(next) => next !== theme && toggleTheme()} options={[{ value: "light", label: "Light" }, { value: "dark", label: "Dark" }]} />
          </Field>
          <Field label="Color scheme">
            <ColorSchemePicker value={colorScheme} onChange={setColorScheme} showLabels />
          </Field>
        </div>
        {appearanceError && <p role="alert" className="mt-3 text-xs text-ink-red-600 dark:text-ink-red-400">{appearanceError}</p>}
      </ProfileSection>
      <ProfileSection title="Language & region" description="Controls response conventions and localization.">
        <Select
          className="max-w-xs"
          value={profile?.localization?.locale || "en-US"}
          onChange={(value) => mutation.mutate({ action: "locale", payload: value })}
          options={LOCALE_OPTIONS}
          disabled={mutation.isPending && mutation.variables?.action === "locale"}
        />
      </ProfileSection>
      <ProfileSection title="My Style" description="Save up to 10 named presentation styles and choose a global default.">
        <div className="flex items-center justify-between"><p className="text-sm text-ink-gray-500 dark:text-ink-gray-400">{styles.length} of 10 styles</p><CompactPrimaryButton type="button" disabled={styles.length >= 10 || editing} onClick={() => setEditing({ ...EMPTY_STYLE })}><Plus className="h-4 w-4" />New style</CompactPrimaryButton></div>
        {(styles.length > 0 || !editing) && (
          <div className="mt-4 divide-y divide-edge-gray-200 dark:divide-edge-gray-700 border-y border-edge-gray-200 dark:border-edge-gray-700">
            {styles.map((style) => <div key={style.style_id} className="flex items-center gap-3 py-3"><button type="button" title="Set as global default" onClick={() => mutation.mutate({ action: "activate", payload: style.style_id })} className={`grid h-7 w-7 place-items-center rounded-full border ${profile.active_style_id === style.style_id ? "border-edge-primary bg-fill-primary text-ink-on-primary" : "border-edge-gray-300 text-transparent"}`}><Check className="h-4 w-4" /></button><div className="min-w-0 flex-1"><p className="truncate text-sm font-medium text-ink-gray-900 dark:text-ink-white">{style.name}</p><p className="text-xs capitalize text-ink-gray-500 dark:text-ink-gray-400">{style.response_length} · {style.tone}</p></div><button type="button" title="Edit style" onClick={() => setEditing(style)} className="p-2 text-ink-gray-500 hover:text-ink-gray-900 dark:hover:text-ink-white"><Edit3 className="h-4 w-4" /></button><button type="button" title="Delete style" onClick={() => setDeletingStyle(style)} className="p-2 text-ink-gray-500 hover:text-ink-red-600"><Trash2 className="h-4 w-4" /></button></div>)}
            {!styles.length && <p className="py-6 text-center text-sm text-ink-gray-500 dark:text-ink-gray-400">No saved styles yet.</p>}
          </div>
        )}
        <AnimatePresence initial={false}>
          {editing && <StyleEditor key={editing.style_id || "new"} initialStyle={editing} saving={mutation.isPending} onCancel={() => setEditing(null)} onSave={(payload) => mutation.mutate({ action: editing.style_id ? "update" : "create", payload })} />}
        </AnimatePresence>
        {profile.active_style_id && <button type="button" onClick={() => mutation.mutate({ action: "activate", payload: null })} className="mt-4 text-sm font-medium text-ink-primary dark:text-ink-accent">Clear global default</button>}
      </ProfileSection>
      <ConfirmDialog
        open={!!deletingStyle}
        title="Delete style?"
        confirmLabel="Delete"
        loadingLabel="Deleting…"
        isLoading={mutation.isPending}
        onCancel={() => setDeletingStyle(null)}
        onConfirm={() => mutation.mutate({ action: "delete", payload: deletingStyle.style_id })}
      >
        Delete <span className="font-semibold text-ink-gray-700 dark:text-ink-gray-300">{deletingStyle?.name}</span>? This cannot be undone.
      </ConfirmDialog>
    </SettingsPageShell>
  );
};

export default ProfilePage;